import { CommonModule } from "@angular/common";
import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  signal,
  SimpleChanges,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { PdvTerminalSales } from "@models/pdv-session";
import { PdvSessionsService } from "@services/pdv-sessions.service";
import { hasInputChanged } from "@shared/helpers/hasChanges/hasChanges.helper";
import { catchError, EMPTY, finalize, take, tap } from "rxjs";
import { TableModule } from "primeng/table";
import { ButtonComponent } from "../../../../shared/components/button/button.component";

@Component({
  selector: "app-pdv-terminal-sales",
  imports: [CommonModule, ReactiveFormsModule, TableModule, ButtonComponent],
  templateUrl: "./pdv-terminal-sales.component.html",
  styleUrl: "./pdv-panel.scss",
})
export class PdvTerminalSalesComponent implements OnChanges {
  @Input({ required: true }) eventId!: number;
  private readonly service = inject(PdvSessionsService);
  private readonly destroyRef = inject(DestroyRef);
  private request = 0;
  readonly terminal = new FormControl("", { nonNullable: true });
  readonly result = signal<PdvTerminalSales | null>(null);
  readonly terminals = signal<PdvTerminalSales["terminals"]>([]);
  readonly hasUnidentified = signal(false);
  readonly loading = signal(false);
  readonly error = signal("");
  readonly paymentNames: Record<string, string> = {
    cash: "Dinheiro",
    credit: "Crédito",
    credit_card: "Crédito",
    debit: "Débito",
    pix_stone: "PIX",
  };
  readonly statusNames: Record<string, string> = {
    paid: "Pago",
    refunded: "Reembolsado",
    canceled: "Cancelado",
    cancelled: "Cancelado",
  };

  constructor() {
    this.terminal.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.load(1)),
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.verifyEvent(changes);
  }

  private verifyEvent(changes: SimpleChanges) {
    if (
      !hasInputChanged<PdvTerminalSalesComponent, "eventId">(changes, "eventId")
    )
      return;
    this.terminal.setValue("", { emitEvent: false });
    this.result.set(null);
    this.terminals.set([]);
    this.hasUnidentified.set(false);
    this.load(1);
  }

  load(page = this.result()?.meta.current_page ?? 1) {
    if (!this.eventId) return;
    const request = ++this.request;
    this.loading.set(true);
    this.error.set("");
    this.result.set(null);
    this.service
      .terminalSales(this.eventId, page, this.terminal.value)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        tap((result) => {
          if (request !== this.request) return;
          this.result.set(result);
          this.terminals.set(result.terminals);
          this.hasUnidentified.set(result.has_unidentified);
        }),
        catchError(() => {
          if (request === this.request)
            this.error.set(
              "Não foi possível carregar a origem das vendas. Tente novamente.",
            );
          return EMPTY;
        }),
        finalize(() => {
          if (request === this.request) this.loading.set(false);
        }),
      )
      .subscribe();
  }
}
